import { useEffect, useMemo, useState } from 'react';
import { Eye, Trash2 } from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import { signOutUser } from '../auth';
import { PageLoadingSkeleton } from '../components/PageLoadingSkeleton';
import { getUserFacingError } from '../lib/errors';
import { formatCurrency } from '../lib/formatters';

const accountTabs = [
  { id: 'overview', label: 'Profile' },
  { id: 'orders', label: 'Orders' },
  { id: 'reviews', label: 'Reviews' },
  { id: 'billing', label: 'Billing' },
];

const orderStatusTabs = [
  { id: 'active', label: 'Active' },
  { id: 'missing_details', label: 'Waiting on buyer' },
  { id: 'delivered', label: 'Delivered' },
  { id: 'completed', label: 'Completed' },
  { id: 'cancelled', label: 'Cancelled' },
  { id: 'all', label: 'All' },
];

const initialProfile = {
  fullName: '',
  marketplaceEmail: '',
  username: '',
  headline: '',
  bio: '',
  stripeAccountId: '',
  stripeOnboardingComplete: false,
  stripePayoutsEnabled: false,
  responseRate: 100,
  completionRate: 100,
};

function normalizeOrderStatus(status) {
  const normalized = String(status || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/-/g, '_');

  if (['missing_details', 'awaiting_details', 'needs_details'].includes(normalized)) {
    return 'missing_details';
  }

  if (['delivered', 'submitted'].includes(normalized)) {
    return 'delivered';
  }

  if (['completed', 'closed'].includes(normalized)) {
    return 'completed';
  }

  if (['cancelled', 'canceled'].includes(normalized)) {
    return 'cancelled';
  }

  return 'active';
}

function formatOrderStatus(status) {
  return status
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatListingStatus(status) {
  if (status === 'published') {
    return 'Live';
  }

  if (status === 'draft') {
    return 'Pending Stripe';
  }

  return 'Archived';
}

function formatOrderDate(value) {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function AccountPage({
  currentUser,
  snapshot,
  loading,
  authLoading,
  onLoadProfile,
  onUpdateProfile,
  onDeleteListing,
  onCreateStripeConnectLink,
}) {
  const [profile, setProfile] = useState(initialProfile);
  const [savedProfile, setSavedProfile] = useState(initialProfile);
  const [profileStatus, setProfileStatus] = useState('');
  const [busyListingId, setBusyListingId] = useState('');
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [showProfileSavedDialog, setShowProfileSavedDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [activeOrderStatus, setActiveOrderStatus] = useState('active');
  const [orderSearch, setOrderSearch] = useState('');
  const [hideCompletedOnboarding, setHideCompletedOnboarding] = useState(false);

  const myListings = useMemo(() => {
    if (!snapshot || !currentUser) {
      return [];
    }

    return snapshot.listings.filter((listing) => listing.seller?.id === currentUser.id);
  }, [currentUser, snapshot]);
  const liveListingCount = useMemo(
    () => myListings.filter((listing) => listing.status === 'published').length,
    [myListings]
  );
  const averageResponseHours = useMemo(() => {
    const responseValues = myListings
      .map((listing) => Number(listing.responseHours))
      .filter((value) => Number.isFinite(value) && value > 0);

    if (responseValues.length === 0) {
      return null;
    }

    return Math.round(responseValues.reduce((sum, value) => sum + value, 0) / responseValues.length);
  }, [myListings]);

  const onboardingItems = useMemo(
    () => [
      { id: 'fullName', label: 'Add your full name', complete: Boolean(profile.fullName?.trim()) },
      { id: 'username', label: 'Claim your @username', complete: Boolean(profile.username?.trim()) },
      { id: 'headline', label: 'Write a seller headline', complete: Boolean(profile.headline?.trim()) },
      { id: 'bio', label: 'Add a short bio', complete: Boolean(profile.bio?.trim()) },
      { id: 'listing', label: 'Publish your first agent', complete: myListings.length > 0 },
    ],
    [myListings.length, profile.bio, profile.fullName, profile.headline, profile.username]
  );

  const onboardingProgress = useMemo(() => {
    const completed = onboardingItems.filter((item) => item.complete).length;
    return Math.round((completed / onboardingItems.length) * 100);
  }, [onboardingItems]);

  const remainingOnboardingItems = onboardingItems.filter((item) => !item.complete);
  const isOnboardingComplete = onboardingProgress >= 100;

  const accountOrders = useMemo(() => {
    if (!snapshot || !Array.isArray(snapshot.orders)) {
      return [];
    }

    return snapshot.orders.map((order) => ({
      ...order,
      listingTitle: order.listingTitle || order.listing?.title || 'Untitled agent',
      counterpartyName:
        order.counterparty?.name ||
        (order.role === 'seller' ? order.buyerName || order.buyer?.name : order.seller?.name) ||
        'Marketplace user',
      totalAmount: Number(order.totalAmount ?? 0) || 0,
      dueDate: formatOrderDate(order.dueDate),
      status: normalizeOrderStatus(order.status),
    }));
  }, [snapshot]);

  const filteredOrders = useMemo(() => {
    return accountOrders.filter((order) => {
      const matchesStatus = activeOrderStatus === 'all' || order.status === activeOrderStatus;
      const searchValue = orderSearch.trim().toLowerCase();
      const matchesSearch =
        searchValue.length === 0 ||
        order.id.toLowerCase().includes(searchValue) ||
        order.listingTitle.toLowerCase().includes(searchValue);
      return matchesStatus && matchesSearch;
    });
  }, [accountOrders, activeOrderStatus, orderSearch]);

  const sellerOrders = useMemo(() => accountOrders.filter((order) => order.role === 'seller'), [accountOrders]);

  const billingSummary = useMemo(() => {
    return sellerOrders.reduce(
      (summary, order) => {
        if (order.status !== 'cancelled') {
          summary.lifetimeRevenue += order.totalAmount;
        }

        if (['active', 'delivered', 'missing_details'].includes(order.status)) {
          summary.pendingPayouts += order.totalAmount;
        }

        if (order.pricingType === 'subscription') {
          summary.subscriptionOrders += 1;
        }

        return summary;
      },
      {
        lifetimeRevenue: 0,
        pendingPayouts: 0,
        subscriptionOrders: 0,
      }
    );
  }, [sellerOrders]);

  const stripeStatus = useMemo(() => {
    if (profile.stripePayoutsEnabled) {
      return {
        title: 'Connected',
        copy: 'Your payout account is connected and ready to receive agent earnings.',
        buttonLabel: 'View Stripe Dashboard',
      };
    }

    if (profile.stripeOnboardingComplete) {
      return {
        title: 'Under review',
        copy:
          'Your Stripe account details were submitted. Stripe is reviewing the account before payouts are enabled.',
        buttonLabel: 'Open Stripe Status',
      };
    }

    if (profile.stripeAccountId) {
      return {
        title: 'Setup incomplete',
        copy: 'Finish Stripe onboarding so your agents can accept payments and receive payouts.',
        buttonLabel: 'Continue Stripe Setup',
      };
    }

    return {
      title: 'Not connected',
      copy: 'Connect Stripe Express so AgenticHire can collect payments and send you payouts.',
      buttonLabel: 'Connect Stripe',
    };
  }, [profile.stripeAccountId, profile.stripeOnboardingComplete, profile.stripePayoutsEnabled]);

  const activeOrderTabLabel = orderStatusTabs.find((tab) => tab.id === activeOrderStatus)?.label || 'Active';
  const orderPanelTitle = `${activeOrderTabLabel.toUpperCase()} ORDERS`;

  const sellerReviews = useMemo(() => {
    return myListings
      .flatMap((listing) =>
        (listing.reviews || []).map((review) => ({
          ...review,
          listingId: listing.id,
          listingSlug: listing.slug,
          listingTitle: listing.title,
        }))
      )
      .sort((left, right) => (right.id || '').localeCompare(left.id || ''));
  }, [myListings]);

  const reviewSummary = useMemo(() => {
    if (sellerReviews.length === 0) {
      return {
        averageRating: 0,
        totalReviews: 0,
        listingsWithReviews: 0,
        fiveStarReviews: 0,
      };
    }

    const totalRating = sellerReviews.reduce((sum, review) => sum + Number(review.rating || 0), 0);
    const reviewedListingIds = new Set(sellerReviews.map((review) => review.listingId).filter(Boolean));

    return {
      averageRating: totalRating / sellerReviews.length,
      totalReviews: sellerReviews.length,
      listingsWithReviews: reviewedListingIds.size,
      fiveStarReviews: sellerReviews.filter((review) => Number(review.rating) === 5).length,
    };
  }, [sellerReviews]);

  useEffect(() => {
    let active = true;

    async function loadProfile() {
      if (!currentUser || !onLoadProfile) {
        return;
      }

      try {
        const result = await onLoadProfile(currentUser);
        if (active && result?.profile) {
          const nextProfile = {
            ...initialProfile,
            ...result.profile,
          };
          setProfile(nextProfile);
          setSavedProfile(nextProfile);
        }
      } catch (error) {
        if (active) {
          setProfileStatus(getUserFacingError(error, 'Unable to load your profile right now.'));
        }
      }
    }

    loadProfile();

    return () => {
      active = false;
    };
  }, [currentUser, onLoadProfile]);

  useEffect(() => {
    if (!isOnboardingComplete) {
      setHideCompletedOnboarding(false);
      return undefined;
    }

    setHideCompletedOnboarding(false);
    const timer = window.setTimeout(() => {
      setHideCompletedOnboarding(true);
    }, 3000);

    return () => window.clearTimeout(timer);
  }, [isOnboardingComplete]);

  useEffect(() => {
    if (!showProfileSavedDialog) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setShowProfileSavedDialog(false);
    }, 4500);

    return () => window.clearTimeout(timer);
  }, [showProfileSavedDialog]);

  if (authLoading) {
    return <PageLoadingSkeleton label="Loading account" />;
  }

  if (!currentUser) {
    return <Navigate replace to="/login?redirect=/account" />;
  }

  if (loading || !snapshot) {
    return <PageLoadingSkeleton label="Loading account" />;
  }

  async function handleProfileSubmit(event) {
    event.preventDefault();

    if (!isEditingProfile) {
      return;
    }

    setProfileStatus('Saving profile...');
    setShowProfileSavedDialog(false);
    setIsSavingProfile(true);

    try {
      const result = await onUpdateProfile(profile);
      const nextProfile = {
        ...initialProfile,
        ...profile,
        ...result.profile,
      };
      setProfile(nextProfile);
      setSavedProfile(nextProfile);
      setIsEditingProfile(false);
      setProfileStatus('');
      setShowProfileSavedDialog(true);
    } catch (error) {
      const message = String(error?.message || '');
      if (message.toLowerCase().includes('profiles_username_unique') || message.toLowerCase().includes('duplicate key')) {
        setProfileStatus('That username is already taken. Choose a different username and try again.');
      } else {
        setProfileStatus(getUserFacingError(error, 'Unable to save your profile right now.'));
      }
    } finally {
      setIsSavingProfile(false);
    }
  }

  function handleStartEditing() {
    setProfileStatus('');
    setShowProfileSavedDialog(false);
    setIsEditingProfile(true);
  }

  function handleCancelEditing() {
    setProfile(savedProfile);
    setProfileStatus('');
    setShowProfileSavedDialog(false);
    setIsEditingProfile(false);
  }

  async function handleDelete(listingId) {
    const confirmed = window.confirm('Delete this listing from the marketplace?');
    if (!confirmed) {
      return;
    }

    setBusyListingId(listingId);
    try {
      await onDeleteListing(listingId);
      setProfileStatus('Listing deleted.');
    } catch (error) {
      setProfileStatus(getUserFacingError(error, 'Unable to delete that listing right now.'));
    } finally {
      setBusyListingId('');
    }
  }

  async function handleSignOut() {
    setProfileStatus('Signing out...');

    try {
      await signOutUser();
    } catch (error) {
      setProfileStatus(getUserFacingError(error, 'Unable to sign out right now.'));
    }
  }

  async function handleStripeConnect() {
    const opensDashboard = profile.stripePayoutsEnabled;
    const dashboardWindow = opensDashboard ? window.open('', '_blank', 'noopener,noreferrer') : null;
    setProfileStatus(opensDashboard ? 'Opening Stripe Dashboard...' : 'Opening Stripe setup...');

    try {
      const result = await onCreateStripeConnectLink();
      if (result?.url) {
        if (result.mode === 'dashboard') {
          if (dashboardWindow) {
            dashboardWindow.location.href = result.url;
          } else {
            window.open(result.url, '_blank', 'noopener,noreferrer');
          }
          setProfileStatus('');
          return;
        }

        dashboardWindow?.close();
        window.location.href = result.url;
        return;
      }
      dashboardWindow?.close();
      setProfileStatus('Unable to open Stripe right now.');
    } catch (error) {
      dashboardWindow?.close();
      setProfileStatus(getUserFacingError(error, 'Unable to open Stripe right now. Please try again.'));
    }
  }

  return (
    <section className="page-shell account-page">
      <div className="page-head account-head">
        <div>
          <span className="eyebrow left">Account</span>
          <h1>Manage your profile and agents.</h1>
          <p>Update your seller details, review your active services, and handle your account in one place.</p>
        </div>
      </div>

      <div className="account-grid">
        <aside className="detail-panel account-side-panel">
          <h2>Overview</h2>

          <div className="account-metric-list">
            <div className="account-metric">
              <span className="metric-label">Marketplace email</span>
              <strong>{profile.marketplaceEmail || currentUser.email}</strong>
            </div>
            <div className="account-metric">
              <span className="metric-label">Active agents</span>
              <strong>{liveListingCount}</strong>
            </div>
            <div className="account-metric">
              <span className="metric-label">Review rating</span>
              <strong className="account-rating-value">
                {reviewSummary.totalReviews > 0 ? reviewSummary.averageRating.toFixed(1) : '0.0'}
              </strong>
            </div>
            <div className="account-metric">
              <span className="metric-label">Avg response time</span>
              <strong>{averageResponseHours !== null ? `${averageResponseHours}h` : 'Not set'}</strong>
            </div>
          </div>

          <div className="account-side-actions">
            <Link to="/studio" className="primary-button ui-button full-width">
              Create new agent
            </Link>
            <button type="button" className="ghost-button ui-button full-width" onClick={handleSignOut}>
              Sign out
            </button>
          </div>
        </aside>

        <div className="account-main">
          <div className="account-tabs" role="tablist" aria-label="Account sections">
            {accountTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`account-tab${activeTab === tab.id ? ' active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'overview' ? (
            <>
              {!hideCompletedOnboarding ? (
                <section className={`detail-panel account-onboarding-panel${isOnboardingComplete ? ' is-complete' : ''}`}>
                  {!isOnboardingComplete ? (
                    <div className="account-onboarding-top">
                      <span className="account-progress-edge">0%</span>
                      <div className="account-progress-track">
                        <div className="account-progress-fill" style={{ width: `${onboardingProgress}%` }} />
                        <div className="account-progress-badge" style={{ left: `min(${onboardingProgress}%, calc(100% - 28px))` }}>
                          {onboardingProgress}%
                        </div>
                      </div>
                      <span className="account-progress-edge">100%</span>
                    </div>
                  ) : null}

                  <div className="account-onboarding-copy">
                    <h2>{isOnboardingComplete ? 'Profile ready to go.' : "You're nearly there, add a few more details to complete your profile."}</h2>
                    {!isOnboardingComplete && remainingOnboardingItems.length > 0 ? (
                      <div className="account-onboarding-list">
                        {remainingOnboardingItems.map((item) => (
                          <span key={item.id} className="outline-pill">
                            {item.label}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </section>
              ) : null}

              <section className="detail-panel account-profile-panel">
                <div className="section-head">
                  <h2>Profile details</h2>
                </div>

                <form className="ui-form account-profile-form" onSubmit={handleProfileSubmit}>
                  <div className="form-grid">
                    <label className="form-field">
                      <span className="form-label">Full name</span>
                      <input
                        className={`ui-input${isEditingProfile ? '' : ' is-readonly'}`}
                        type="text"
                        placeholder="Full name"
                        value={profile.fullName}
                        readOnly={!isEditingProfile}
                        onChange={(event) => setProfile((current) => ({ ...current, fullName: event.target.value }))}
                      />
                    </label>
                    <label className="form-field">
                      <span className="form-label">Marketplace email</span>
                      <input
                        className={`ui-input${isEditingProfile ? '' : ' is-readonly'}`}
                        type="email"
                        placeholder="name@company.com"
                        value={profile.marketplaceEmail}
                        readOnly={!isEditingProfile}
                        onChange={(event) => setProfile((current) => ({ ...current, marketplaceEmail: event.target.value }))}
                      />
                    </label>
                    <label className="form-field">
                      <span className="form-label">@Username</span>
                      <input
                        className={`ui-input${isEditingProfile ? '' : ' is-readonly'}`}
                        type="text"
                        placeholder="username"
                        value={profile.username}
                        readOnly={!isEditingProfile}
                        onChange={(event) =>
                          setProfile((current) => ({
                            ...current,
                            username: event.target.value.replace(/\s+/g, '').replace(/^@+/, ''),
                          }))
                        }
                      />
                    </label>
                    <label className="form-field form-field-wide">
                      <span className="form-label">Headline</span>
                      <input
                        className={`ui-input${isEditingProfile ? '' : ' is-readonly'}`}
                        type="text"
                        placeholder="Headline"
                        value={profile.headline}
                        readOnly={!isEditingProfile}
                        onChange={(event) => setProfile((current) => ({ ...current, headline: event.target.value }))}
                      />
                    </label>
                  </div>

                  <label className="form-field">
                    <span className="form-label">Bio</span>
                    <textarea
                      className={`ui-textarea${isEditingProfile ? '' : ' is-readonly'}`}
                      rows="5"
                      placeholder="Bio"
                      value={profile.bio}
                      readOnly={!isEditingProfile}
                      onChange={(event) => setProfile((current) => ({ ...current, bio: event.target.value }))}
                    />
                  </label>

                  <div className="account-profile-actions">
                    {isEditingProfile ? (
                      <>
                        <button type="button" className="ghost-button ui-button" onClick={handleCancelEditing}>
                          Cancel
                        </button>
                        <button type="submit" className="primary-button ui-button account-save-button" disabled={isSavingProfile}>
                          {isSavingProfile ? 'Saving...' : 'Save profile'}
                        </button>
                      </>
                    ) : (
                      <button type="button" className="primary-button ui-button account-save-button" onClick={handleStartEditing}>
                        Edit
                      </button>
                    )}
                  </div>
                </form>
              </section>

              <section className="detail-panel account-listings-panel reveal-on-load">
                <div className="section-head">
                  <h2>Your Agents</h2>
                  <Link to="/studio" className="account-add-button" aria-label="Create another agent">
                    +
                  </Link>
                </div>

                {myListings.length === 0 ? (
                  <div className="account-empty-state">
                    <p>You do not have any live agents yet.</p>
                  </div>
                ) : (
                  <div className="account-listings-grid">
                    {myListings.map((listing) => (
                      <article key={listing.id} className="account-listing-card">
                        <div className="account-listing-top">
                          <div className="account-listing-pills">
                            <span className="outline-pill">{listing.category?.name || 'Agent'}</span>
                            <span className={`account-status-pill status-${listing.status || 'published'}`}>
                              {formatListingStatus(listing.status || 'published')}
                            </span>
                          </div>
                          <div className="account-listing-top-actions">
                            <Link
                              to={`/agents/${listing.slug}`}
                              className="account-listing-icon-button account-listing-view-button"
                              aria-label={`View ${listing.title}`}
                              title="View listing"
                            >
                              <Eye size={18} />
                            </Link>
                            <button
                              type="button"
                              className="account-listing-icon-button account-listing-delete-button"
                              onClick={() => handleDelete(listing.id)}
                              disabled={busyListingId === listing.id}
                              aria-label={`Delete ${listing.title}`}
                              title={busyListingId === listing.id ? 'Deleting listing' : 'Delete listing'}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>

                        <div className="account-listing-copy">
                          <h3>{listing.title}</h3>
                          <p>{listing.tagline}</p>
                        </div>

                        <div className="account-listing-meta">
                          <div>
                            <span className="metric-label">Price</span>
                            <strong>{formatCurrency(listing.priceFrom)}</strong>
                          </div>
                          <div>
                            <span className="metric-label">Delivery</span>
                            <strong>{listing.deliveryDays} days</strong>
                          </div>
                          <div>
                            <span className="metric-label">Orders</span>
                            <strong>{listing.ordersCompleted}</strong>
                          </div>
                        </div>

                      </article>
                    ))}
                  </div>
                )}
              </section>
            </>
          ) : null}

          {activeTab === 'orders' ? (
            <section className="detail-panel account-orders-section">
              <div className="account-orders-header">
                <div className="account-orders-heading">
                  <h2>Manage Orders</h2>
                  <p>Track agent work in progress, deliveries, and completed engagements from one queue.</p>
                </div>
                <label className="account-order-search">
                  <span className="metric-label">Order ID</span>
                  <input
                    className="ui-input"
                    type="search"
                    placeholder="Search order ID"
                    value={orderSearch}
                    onChange={(event) => setOrderSearch(event.target.value)}
                  />
                </label>
              </div>

              <div className="account-order-tabs" role="tablist" aria-label="Order statuses">
                {orderStatusTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={activeOrderStatus === tab.id}
                    className={`account-order-tab${activeOrderStatus === tab.id ? ' active' : ''}`}
                    onClick={() => setActiveOrderStatus(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="account-orders-board">
                <div className="account-orders-board-head">
                  <strong>{orderPanelTitle}</strong>
                  <span>{filteredOrders.length} shown</span>
                </div>

                {filteredOrders.length === 0 ? (
                  <div className="account-orders-empty">
                    <p>No {activeOrderTabLabel.toLowerCase()} orders to show.</p>
                    <small>
                      Orders will appear here once buyers start purchasing your agents and moving work into delivery.
                    </small>
                  </div>
                ) : (
                  <div className="account-order-table">
                    <div className="account-order-table-head">
                      <span>Order ID</span>
                      <span>Agent</span>
                      <span>Seller</span>
                      <span>Total</span>
                      <span>Due</span>
                      <span>Status</span>
                    </div>
                    {filteredOrders.map((order) => (
                      <article key={order.id} className="account-order-row">
                        <div className="account-order-cell account-order-cell-id">
                          <span className="account-order-mobile-label">Order ID</span>
                          <Link to={`/orders/${order.id}`} className="inline-link">
                            {order.id}
                          </Link>
                        </div>
                        <div className="account-order-cell">
                          <span className="account-order-mobile-label">Agent</span>
                          <span>{order.listingTitle}</span>
                        </div>
                        <div className="account-order-cell">
                          <span className="account-order-mobile-label">Seller</span>
                          <span>{order.counterpartyName}</span>
                        </div>
                        <div className="account-order-cell">
                          <span className="account-order-mobile-label">Total</span>
                          <span>{formatCurrency(order.totalAmount)}</span>
                        </div>
                        <div className="account-order-cell">
                          <span className="account-order-mobile-label">Due</span>
                          <span>{order.dueDate || 'Not set'}</span>
                        </div>
                        <div className="account-order-cell">
                          <span className="account-order-mobile-label">Status</span>
                          <span className={`account-order-status status-${order.status}`}>{formatOrderStatus(order.status)}</span>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </div>
            </section>
          ) : null}

          {activeTab === 'billing' ? (
            <section className="account-billing-section">
              <div className="detail-panel account-billing-panel">
                <div className="section-head">
                  <h2>Billing and payouts</h2>
                </div>

                <div className="account-billing-grid">
                  <article className="account-billing-card">
                    <span className="metric-label">Lifetime revenue</span>
                    <strong>{formatCurrency(billingSummary.lifetimeRevenue)}</strong>
                    <p>All non-cancelled agent orders and subscriptions processed through the marketplace.</p>
                  </article>

                  <article className="account-billing-card">
                    <span className="metric-label">Pending payouts</span>
                    <strong>{formatCurrency(billingSummary.pendingPayouts)}</strong>
                    <p>Funds from active, delivered, or buyer-blocked work that have not cleared to payout yet.</p>
                  </article>

                  <article className="account-billing-card">
                    <span className="metric-label">Subscription orders</span>
                    <strong>{billingSummary.subscriptionOrders}</strong>
                    <p>Recurring agent engagements currently tracked in your billing workspace.</p>
                  </article>

                  <article className="account-billing-card">
                    <span className="metric-label">Stripe payouts</span>
                    <strong>{stripeStatus.title}</strong>
                    <p>{stripeStatus.copy}</p>
                    <button type="button" className="ghost-button ui-button" onClick={handleStripeConnect}>
                      {stripeStatus.buttonLabel}
                    </button>
                  </article>

                  <article className="account-billing-card account-billing-card-wide">
                    <span className="metric-label">Billing activity</span>
                    {sellerOrders.length === 0 ? (
                      <div className="account-billing-empty">
                        <p>No billing activity yet.</p>
                        <small>Once a buyer hires one of your agents, charges, payouts, and billing history will appear here.</small>
                      </div>
                    ) : (
                      <div className="account-billing-empty">
                        <p>Detailed billing activity is ready for the first payout integration pass.</p>
                        <small>Orders already flow into the summary cards above, and this panel can expand into ledger history next.</small>
                      </div>
                    )}
                  </article>
                </div>
              </div>
            </section>
          ) : null}

          {activeTab === 'reviews' ? (
            <section className="account-reviews-section">
              <div className="detail-panel account-reviews-panel">
                <div className="section-head">
                  <h2>Reviews</h2>
                </div>

                <div className="account-reviews-grid">
                  <article className="account-review-card">
                    <span className="metric-label">Average rating</span>
                    <strong>{reviewSummary.totalReviews > 0 ? reviewSummary.averageRating.toFixed(1) : '0.0'}</strong>
                    <p>Average seller rating across all published agent reviews.</p>
                  </article>

                  <article className="account-review-card">
                    <span className="metric-label">Total reviews</span>
                    <strong>{reviewSummary.totalReviews}</strong>
                    <p>All buyer reviews currently attached to your live and past agent listings.</p>
                  </article>

                  <article className="account-review-card">
                    <span className="metric-label">Agents reviewed</span>
                    <strong>{reviewSummary.listingsWithReviews}</strong>
                    <p>How many of your agents have received at least one published buyer review.</p>
                  </article>

                  <article className="account-review-card">
                    <span className="metric-label">5-star reviews</span>
                    <strong>{reviewSummary.fiveStarReviews}</strong>
                    <p>Top-rated buyer feedback you can use to understand what is resonating most.</p>
                  </article>
                </div>

                {sellerReviews.length === 0 ? (
                  <div className="account-reviews-empty">
                    <p>No reviews yet.</p>
                    <small>Once buyers complete agent work and leave feedback, those reviews will appear here for you to monitor.</small>
                  </div>
                ) : (
                  <div className="account-review-list">
                    {sellerReviews.map((review) => (
                      <article key={`${review.listingId || 'listing'}-${review.id}`} className="account-review-item">
                        <div className="account-review-top">
                          <div className="account-review-heading">
                            <strong>{review.reviewerName}</strong>
                            <span>{review.company || 'Independent buyer'}</span>
                          </div>
                          <div className="account-review-rating">
                            <strong>{Number(review.rating || 0).toFixed(1)}</strong>
                            <span>{'★'.repeat(Math.max(0, Math.min(5, Number(review.rating || 0))))}</span>
                          </div>
                        </div>

                        <p>{review.body}</p>

                        <div className="account-review-footer">
                          <span className="outline-pill">{review.listingTitle}</span>
                          <Link to={`/agents/${review.listingSlug}`} className="inline-link">
                            View agent
                          </Link>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </div>
            </section>
          ) : null}

          {profileStatus ? <p className="status-note">{profileStatus}</p> : null}
        </div>
      </div>
      {showProfileSavedDialog ? (
        <div className="account-save-dialog-backdrop" role="presentation">
          <div className="account-save-dialog" role="dialog" aria-modal="true" aria-labelledby="profile-save-title">
            <h2 id="profile-save-title">Profile updated</h2>
            <p>Your profile information was successfully updated.</p>
            <button
              type="button"
              className="primary-button ui-button"
              onClick={() => setShowProfileSavedDialog(false)}
            >
              Done
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
